import {useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import {clearCreateVarietyAction, createVarietyAction, getAllCropsAction, updateVarietyAction, clearUpdateVarietyAction, getAllVarietiesAction, getByVarietyAction} from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import toast from "react-hot-toast";

export default function VarietyInformation({ onClose, varietyId }) {
  const dispatch = useDispatch();

  const { successData: cropsData } = useSelector((state) => state.getAllCropsReducer);
  const { successData: getByVarietyData } = useSelector((state) => state.getByVarietyReducer);
  const { successData, error, errorInfo } = useSelector((state) => state.createVarietyReducer);
  const { successData: updateSuccess, error: updateError, errorInfo: updateErrorInfo } = useSelector((state) => state.updateVarietyReducer);

  const cropOptions = Array.isArray(cropsData?.data?.crops)? cropsData.data.crops : [];
  
  const initialFormValues = useMemo(() => {
    if (varietyId && getByVarietyData?.data) {
      const v = getByVarietyData.data;
      return {
        cropId: v?.cropId || "",
        name: v?.name || "",
        quantity_type: v?.quantity_type || "",
        status: (v?.status || "ACTIVE").toUpperCase() === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE',
      };
    }
    return {
      cropId: "",
      name: "",
      quantity_type: "",
      status: "ACTIVE",
    };
  }, [varietyId, getByVarietyData]);

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (varietyId) {
        dispatch(updateVarietyAction({ endPoint: `variety/${varietyId}`, ...values }));
      } else {
        dispatch(createVarietyAction(values));
      }
    },
  });

  useEffect(() => {
      dispatch(getAllCropsAction());
      if(varietyId){
        dispatch(getByVarietyAction({ endPoint: `variety/${varietyId}` }));
      }
  }, [dispatch, varietyId]);

  // Expect parent to dispatch getByVarietyAction; just consume data here

  useEffect(() => {
      if (successData) {
        toast.success(successData.message);
        dispatch(clearCreateVarietyAction());
          onClose();
        dispatch(getAllVarietiesAction());
      }
  }, [successData, dispatch, onClose]);

  useEffect(() => {
      if (error) {
        toast.error(errorInfo);
        dispatch(clearCreateVarietyAction());
      }
  }, [error, errorInfo, dispatch]);

  // Form values are populated via enableReinitialize + memoized initial values

  // Handle update success/error and refresh list
  useEffect(() => {
      if (updateSuccess) {
        toast.success(updateSuccess?.message || 'Variety updated successfully');
        dispatch(getAllVarietiesAction());
        onClose();
      }
      if (updateError) {
        toast.error(updateErrorInfo || 'Failed to update variety');
      }
      return () => {
        dispatch(clearUpdateVarietyAction());
      };
  }, [updateSuccess, updateError, updateErrorInfo, dispatch, onClose]);
  

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      
      }}
    >
      <Paper
        elevation={0}
        sx={{
            width: '100%',
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          mt: { xs: 0, sm: 6, md: 0 },
          boxSizing: 'border-box',
          overflowY: 'auto',
        }}
      >
        <form onSubmit={formik.handleSubmit}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600} fontSize={{ xs: 18, sm: 22 }}>
            {varietyId ? 'Edit Variety' : 'Variety Information'}
          </Typography>
            <IconButton size="small" onClick={onClose} sx={{ ml: 3 }}>
              <CloseIcon />
            </IconButton>
        </Box>

        {/* Crop ID & Variety Name */}
        <Box
          display={{ xs: "block", sm: "flex" }}
          gap={3}
          mb={3}
        >
          <Box flex={1} mb={{ xs: 3, sm: 0 }}>
            <Typography fontSize={14} mb={1}>
              Crop
            </Typography>
            <Autocomplete
              options={cropOptions}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.cropId === value.cropId}
              value={cropOptions.find((c) => c.cropId === formik.values.cropId) || null}
              onChange={(_, selected) => {
                formik.setFieldValue("cropId", selected ? selected.cropId : "");
              }}
              onBlur={() => formik.setFieldTouched("cropId", true)}
              noOptionsText="No crops found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  placeholder="Search and select crop"
                  error={formik.touched.cropId && Boolean(formik.errors.cropId)}
                  helperText={formik.touched.cropId && formik.errors.cropId}
                />
              )}
            />
          </Box>

          <Box flex={1}>
            <Typography fontSize={14} mb={1}>
              Variety Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Box>
        </Box>

        {/* QTY Type & Status */}
        <Box
          display={{ xs: "block", sm: "flex" }}
          gap={3}
          mb={5}
        >
          <Box flex={1}>
            <Typography fontSize={14} mb={1}>
              QTY Type
            </Typography>
            <TextField
              fullWidth
              size="small"
              select
              name="quantity_type"
              value={formik.values.quantity_type}
              onChange={formik.handleChange}
              placeholder="Select QTY Type"
            >
              <MenuItem value="Packets">Packets</MenuItem>
              <MenuItem value="Kgs">Kgs</MenuItem>
            </TextField>
          </Box>
          <Box flex={1}>
            <Typography fontSize={14} mb={1}>
              Status
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  name="status"
                  checked={formik.values.status === "ACTIVE"}
                  onChange={(e) => formik.setFieldValue('status', e.target.checked ? "ACTIVE" : "INACTIVE")}
                />
              }
              label={formik.values.status === "ACTIVE" ? "Active" : "Inactive"}
            />
          </Box>
        </Box>

        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
          <Button
           
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              py: 1.3,
            }}
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              py: 1.3,
              background: "#4F46E5",
            }}
            type="submit"
          >
            Submit 
          </Button>
        </Box>
        </form>
      </Paper>
    </Box>
  );
}

VarietyInformation.propTypes = {
  onClose: PropTypes.func.isRequired,
  varietyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

